"use client";

import Link from "next/link";
import { FormattedMessage } from "react-intl";
import { FaApple, FaDesktop } from "react-icons/fa6";

import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import GooglePlayIcon from "@/app/components/Utils/GooglePlayIcon";
import { useAppLink } from "@/lib/analytics/app-links";

// 44 px tall on phones and tablets (touch targets); unchanged from lg.
const STORE_BUTTON_CLASS =
  "lift-hit inline-flex max-lg:min-h-11 items-center gap-2 bg-surface border border-gray-light rounded-full px-5 py-2.5 text-text-main font-bold shadow-sm transition duration-[400ms] ease-in-out hover:-translate-y-0.5 hover:border-blue-main hover:text-brand hover:shadow-md";

/**
 * Store availability line shown under the hero video: web, iOS and Android
 * links plus the devices reassurance point.
 */
export default function StoreBadges() {
  const web = useAppLink("hero-badge", { target: "web" });
  const ios = useAppLink("hero-badge", { target: "ios" });
  const android = useAppLink("hero-badge", { target: "android" });
  return (
    <div className="mt-12 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs sm:text-sm lg:text-base">
      <span className="font-semibold text-gray-main whitespace-nowrap">
        <FormattedMessage id="hero.availableOn" />
      </span>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Link
          href={web.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("hero-web-button", {}, { criticalOnNav: true });
            trackCtaClick("hero-badge", "web");
          }}
          className={STORE_BUTTON_CLASS}
        >
          <FaDesktop className="text-lg shrink-0" aria-hidden="true" />
          <FormattedMessage id="hero.web" />
        </Link>
        <Link
          href={ios.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("hero-appstore-button", {}, { criticalOnNav: true });
            trackCtaClick("hero-badge", "ios");
          }}
          className={STORE_BUTTON_CLASS}
        >
          <FaApple className="text-lg shrink-0" aria-hidden="true" />
          <FormattedMessage id="hero.appStore" />
        </Link>
        <Link
          href={android.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("hero-googleplay-button", {}, { criticalOnNav: true });
            trackCtaClick("hero-badge", "android");
          }}
          className={STORE_BUTTON_CLASS}
        >
          <GooglePlayIcon className="w-4 h-4 shrink-0" />
          <FormattedMessage id="hero.googlePlay" />
        </Link>
      </div>

      <span aria-hidden="true" className="hidden text-blue-sec shrink-0 xl:inline">
        •
      </span>
      <span className="text-gray-main whitespace-nowrap">
        <FormattedMessage id="hero.devices" />
      </span>
    </div>
  );
}
